/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import AppTextInput from '../../../../shared/components/app-text-input/AppTextInput';
import AppSelect from '../../../../shared/components/app-select/AppSelect';
import {
  ADULT_TITLE_OPTIONS,
  CHILD_TITLE_OPTIONS,
  PACKAGE_AGE_LIMITS,
} from '../../constants/packageConstants';
import {
  DIAL_OPTIONS,
  dialCodeOf,
  maxMobileDigits,
} from '../../utils/dialCodes';
import {
  PASSENGER_TYPE_LABEL,
  sanitizeDigits,
  sanitizeName,
} from '../../utils/packageUtils';
import './PackagePassengerForm.scss';

export const emptyPassenger = (type = 'ADULT') => ({
  type,
  title: type === 'ADULT' ? 'Mr' : 'Mstr',
  firstName: '',
  lastName: '',
  age: '',
  email: '',
  mobileCountry: 'IN',
  dialCode: '+91',
  mobile: '',
});

/**
 * One traveller as on their ID. The lead (index 0) also carries the contact
 * details the supplier uses. Errors are keyed `p{index}-{field}` and owned by
 * the page so submit can validate everyone at once.
 *
 * Errors are rendered here rather than through AppTextInput's errorText,
 * which only shows while the field is empty — "invalid email" would never
 * appear.
 */
function PackagePassengerForm({ index, passenger, onChange, errors, lead }) {
  const p = passenger;
  const key = (field) => `p${index}-${field}`;
  const err = (field) => errors[key(field)] || '';
  const set = (patch) => onChange(index, patch);
  const limits = PACKAGE_AGE_LIMITS[p.type] || PACKAGE_AGE_LIMITS.ADULT;

  return (
    <div className="pkg-passenger" id={`pkg-passenger-${index}`}>
      <div className="pkg-passenger__tag">
        <span className="pkg-passenger__num">
          {String(index + 1).padStart(2, '0')}
        </span>
        {PASSENGER_TYPE_LABEL[p.type] || 'Adult'}
        {lead && <em>Lead traveller</em>}
      </div>

      <div className="pkg-passenger__grid">
        <div className="pkg-passenger__field pkg-passenger__field--title">
          <AppSelect
            name={key('title')}
            label="Title"
            required
            isClearable={false}
            isSearchable={false}
            placeholder=""
            options={
              p.type === 'ADULT' ? ADULT_TITLE_OPTIONS : CHILD_TITLE_OPTIONS
            }
            selectedValue={p.title}
            onSelect={(opt) => set({ title: opt ? opt.value : '' })}
          />
          {err('title') && (
            <span className="pkg-field-error">{err('title')}</span>
          )}
        </div>

        <div className="pkg-passenger__field">
          <AppTextInput
            name={key('first')}
            label="First Name"
            required
            autoComplete="off"
            value={p.firstName}
            onChange={(e) => set({ firstName: sanitizeName(e.target.value) })}
            className={err('first') ? 'error-border' : ''}
          />
          {err('first') && (
            <span className="pkg-field-error">{err('first')}</span>
          )}
        </div>

        <div className="pkg-passenger__field">
          <AppTextInput
            name={key('last')}
            label="Last Name"
            required
            autoComplete="off"
            value={p.lastName}
            onChange={(e) => set({ lastName: sanitizeName(e.target.value) })}
            className={err('last') ? 'error-border' : ''}
          />
          {err('last') && (
            <span className="pkg-field-error">{err('last')}</span>
          )}
        </div>

        <div className="pkg-passenger__field pkg-passenger__field--age">
          <AppTextInput
            name={key('age')}
            label={`Age (${limits.min}-${limits.max})`}
            required
            inputMode="numeric"
            autoComplete="off"
            maxLength={3}
            value={p.age}
            onChange={(e) => set({ age: sanitizeDigits(e.target.value, 3) })}
            className={err('age') ? 'error-border' : ''}
          />
          {err('age') && <span className="pkg-field-error">{err('age')}</span>}
        </div>

        {lead && (
          <>
            <div className="pkg-passenger__field pkg-passenger__field--email">
              <AppTextInput
                name={key('email')}
                label="Email"
                required
                type="email"
                autoComplete="email"
                value={p.email}
                onChange={(e) =>
                  set({ email: e.target.value.trim().slice(0, 100) })
                }
                className={err('email') ? 'error-border' : ''}
              />
              {err('email') && (
                <span className="pkg-field-error">{err('email')}</span>
              )}
            </div>

            <div className="pkg-passenger__field pkg-passenger__field--code">
              <AppSelect
                name={key('mobileCountry')}
                label="Code"
                required
                isClearable={false}
                placeholder=""
                options={DIAL_OPTIONS}
                selectedValue={p.mobileCountry}
                maxheight="240px"
                onSelect={(opt) => {
                  const code = opt ? opt.value : 'IN';
                  set({
                    mobileCountry: code,
                    dialCode: dialCodeOf(code),
                    // a number valid for one country rarely is for another
                    mobile: code === p.mobileCountry ? p.mobile : '',
                  });
                }}
              />
            </div>

            <div className="pkg-passenger__field pkg-passenger__field--mobile">
              <AppTextInput
                name={key('mobile')}
                label="Mobile Number"
                required
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                value={p.mobile}
                onChange={(e) =>
                  set({
                    mobile: sanitizeDigits(
                      e.target.value,
                      maxMobileDigits(p.mobileCountry),
                    ),
                  })
                }
                className={err('mobile') ? 'error-border' : ''}
              />
              {err('mobile') && (
                <span className="pkg-field-error">{err('mobile')}</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

PackagePassengerForm.propTypes = {
  index: PropTypes.number.isRequired,
  passenger: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  lead: PropTypes.bool,
};

PackagePassengerForm.defaultProps = {
  lead: false,
};

export default PackagePassengerForm;
